import { Grid, GridConfig } from '../core/Grid.js';
import { Position } from '../core/GridGeometry.js';
import { ScoreBreakdown } from '../core/Scoring.js';
import { PersistenceManager } from '../persistence/PersistenceManager.js';
import { GameType, INITIAL_TO_GAME, GAME_INITIALS, gridGameSet, GameState, SavedGrid, ChallengeRef } from '../persistence/types.js';
import { isDefaultGrid } from '../persistence/DefaultGrids.js';
import { IGame, GAME_REGISTRY } from '../games/GameRegistry.js';
import { isVortexGame } from '../games/IVortexGame.js';
import { VortexHub } from '../vortex/VortexHub.js';
import { renderGridPreview } from '../rendering/GridPreview.js';

export interface GridSelectorCallbacks {
    onGridSelected: (gridId: string) => void;
    onClose: () => void;
    onCreateNew: () => void;
    onChallengeSelected: (challenge: ChallengeRef) => void;
    showMessage: (message: string) => void;
    /**
     * Open the duplicate modal for a grid: pick the copy's game set, and whether its
     * solved games carry over. The App owns the modal (it shares the center column).
     */
    onDuplicate: (savedGrid: SavedGrid) => void;
    /**
     * A pasted grid code (no solutions) — the App opens the same modal so the set can be
     * chosen before the grid is added. `gameSet` is present only for a challenge code.
     */
    onImportGrid: (gridData: string, gridConfig: GridConfig, gameSet?: string) => void;
    /**
     * Adopt a grid into the authoring backlog. Only wired on the authoring instance
     * (VITE_ADMIN); when absent, the Add as Candidate button is not rendered at all.
     */
    onAddCandidate?: (savedGrid: SavedGrid) => void;
}

/** A folder's identity (also the key used to remember its collapsed state). */
type FolderKey = 'default' | 'past' | 'custom';

export class GridSelector {
    private containerEl: HTMLElement;
    private persistence: PersistenceManager;
    private callbacks: GridSelectorCallbacks;
    private currentGridId: string | null = null;
    private deleteConfirmPendingId: string | null = null;
    private expandedCardId: string | null = null;

    /** Collapsed folders (Past Challenges starts collapsed; the rest expanded). */
    private collapsedFolders: Set<FolderKey> = new Set(['past']);
    /** The latest challenge per track, shown above the folders (Base, then Vortex). */
    private currentChallenges: ChallengeRef[] = [];

    constructor(
        containerEl: HTMLElement,
        persistence: PersistenceManager,
        callbacks: GridSelectorCallbacks
    ) {
        this.containerEl = containerEl;
        this.persistence = persistence;
        this.callbacks = callbacks;
    }

    setCurrentGridId(gridId: string | null): void {
        this.currentGridId = gridId;
    }

    /**
     * The challenge cards pinned above the folders — at most one per track, since
     * Base and Vortex publish in parallel and a player can have one of each open.
     * Empty hides them.
     */
    setCurrentChallenges(challenges: ChallengeRef[]): void {
        this.currentChallenges = challenges;
    }

    /** Section label for a pinned challenge card, by track. */
    private challengeLabel(challenge: ChallengeRef): string {
        return challenge.isVortex ? 'Latest Vortex challenge' : 'Latest challenge';
    }

    show(): void {
        this.render();
        this.containerEl.classList.remove('hidden');
    }

    hide(): void {
        this.deleteConfirmPendingId = null;
        this.expandedCardId = null;
        this.containerEl.classList.add('hidden');
    }

    /** Re-render in place after something outside the selector changed a grid's state. */
    refresh(): void {
        if (!this.containerEl.classList.contains('hidden')) this.render();
    }

    private render(): void {
        const grids = this.persistence.getGridsSorted();
        // Use persistence as the source of truth for "current" so the pinned card
        // always matches getGridsSorted()'s current-first ordering (the externally
        // set currentGridId can lag behind).
        const currentId = this.persistence.getCurrentGridId() ?? this.currentGridId;
        const current = currentId ? grids.find(g => g.id === currentId) ?? null : null;

        // render() rebuilds the whole list, which discards the scrolled element and would
        // otherwise snap the list back to the top on every expand/collapse. Carry the
        // offset across the rebuild.
        const prevScrollTop = this.containerEl.querySelector('.grid-scroll')?.scrollTop ?? 0;

        this.containerEl.innerHTML = '';

        const scroll = document.createElement('div');
        scroll.className = 'grid-scroll';

        // The grid currently being played, pinned at the very top. When it *is* one of
        // the latest challenges, the label says so and that track's card is not
        // repeated below (the other track's still is).
        const playingChallenge = this.currentChallenges.find(c => c.gridData === current?.gridData) ?? null;
        if (current) {
            scroll.appendChild(this.sectionLabel(
                playingChallenge ? `Now playing · ${this.challengeLabel(playingChallenge).toLowerCase()}` : 'Now playing'
            ));
            const card = this.renderGridCard(current);
            card.classList.add('grid-pinned'); // inset to align with folder cards
            scroll.appendChild(card);
        }

        // The latest challenge of each track, just below "Now playing" — each only
        // when it differs from the grid currently being played. Render as a full grid
        // card (status + normal click-to-play) when it's in the portfolio (auto-added
        // on boot); fall back to a minimal card only if it couldn't be added.
        const pinnedChallengeGrids: SavedGrid[] = [];
        for (const challenge of this.currentChallenges) {
            if (challenge === playingChallenge) continue;
            const challengeGrid = grids.find(g => g.gridData === challenge.gridData) ?? null;
            if (challengeGrid) pinnedChallengeGrids.push(challengeGrid);
            scroll.appendChild(this.sectionLabel(this.challengeLabel(challenge)));
            const cc = challengeGrid ? this.renderGridCard(challengeGrid) : this.renderChallengeCard(challenge);
            cc.classList.add('grid-pinned');
            scroll.appendChild(cc);
        }

        // Everything else, grouped into folders (pinned cards are excluded). Challenge
        // grids (other than the pinned latest) form "Past Challenges".
        const pinned = new Set<string>();
        if (current) pinned.add(current.id);
        for (const g of pinnedChallengeGrids) pinned.add(g.id);
        const rest = grids.filter(g => !pinned.has(g.id));
        const defaults = rest.filter(g => isDefaultGrid(g.gridData));
        const past = rest.filter(g => !isDefaultGrid(g.gridData) && g.challengeId != null);
        const custom = rest.filter(g => !isDefaultGrid(g.gridData) && g.challengeId == null);

        scroll.appendChild(this.renderFolder('default', 'Default Grids', defaults));
        scroll.appendChild(this.renderFolder('past', 'Past Challenges', past));
        scroll.appendChild(this.renderCreateRow()); // sits just above Custom Grids
        scroll.appendChild(this.renderFolder('custom', 'Custom Grids', custom));

        // Load Grid + Remove-empty rows live inside the scroll area so they
        // scroll with the content rather than being pinned at the bottom.
        scroll.appendChild(this.renderImportSection());
        scroll.appendChild(this.renderRemoveRow());

        this.containerEl.appendChild(scroll);
        if (prevScrollTop) scroll.scrollTop = prevScrollTop;
    }

    private sectionLabel(text: string): HTMLElement {
        const el = document.createElement('div');
        el.className = 'grid-section-label';
        el.textContent = text;
        return el;
    }

    private renderFolder(key: FolderKey, title: string, cards: SavedGrid[]): HTMLElement {
        const folder = document.createElement('div');
        folder.className = 'grid-folder';
        const collapsed = this.collapsedFolders.has(key);
        if (collapsed) folder.classList.add('collapsed');

        const header = document.createElement('div');
        header.className = 'grid-folder-header';
        const tri = document.createElement('span');
        tri.className = 'grid-folder-tri';
        tri.textContent = collapsed ? '▸' : '▾';
        const label = document.createElement('span');
        label.className = 'grid-folder-label';
        label.textContent = title;
        const count = document.createElement('span');
        count.className = 'grid-folder-count';
        count.textContent = String(cards.length);
        header.append(tri, label, count);
        header.addEventListener('click', () => {
            if (collapsed) this.collapsedFolders.delete(key);
            else this.collapsedFolders.add(key);
            this.render();
        });
        folder.appendChild(header);

        if (!collapsed) {
            const body = document.createElement('div');
            body.className = 'grid-folder-body';
            if (cards.length === 0) {
                const empty = document.createElement('div');
                empty.className = 'grid-folder-empty';
                empty.textContent = key === 'past' ? 'No past challenges yet' : 'Empty';
                body.appendChild(empty);
            }
            for (const savedGrid of cards) body.appendChild(this.renderGridCard(savedGrid));
            folder.appendChild(body);
        }
        return folder;
    }

    /** Build one grid card (preview + scores + expand/action row). */
    private renderGridCard(savedGrid: SavedGrid): HTMLElement {
        const totalScore = this.persistence.getTotalScore(savedGrid.id);
        const isVortex = savedGrid.gridConfig?.isVortex === true;
        const isExpanded = this.expandedCardId === savedGrid.id;

        // Wrapper for card + action row
        const wrapper = document.createElement('div');
        wrapper.dataset.gridId = savedGrid.id;
        wrapper.className = 'grid-option-wrapper';
        if (isExpanded) wrapper.classList.add('expanded');
        if (savedGrid.isImported) wrapper.classList.add('imported');
        if (isVortex) wrapper.classList.add('vortex');

        const option = document.createElement('div');
        option.className = 'grid-option';
        if (savedGrid.id === this.currentGridId) {
            option.classList.add('current');
        }
        if (savedGrid.isImported) {
            option.classList.add('imported');
        }
        if (isVortex) {
            option.classList.add('vortex');
        }

        // Preview canvas
        const preview = document.createElement('canvas');
        preview.className = 'grid-option-preview';
        preview.width = 80;
        preview.height = 80;
        renderGridPreview(preview, savedGrid.gridData, { isImported: !!savedGrid.isImported, isVortex });

        // Info section
        const info = document.createElement('div');
        info.className = 'grid-option-info';

        // Total score
        const totalEl = document.createElement('div');
        totalEl.className = 'grid-option-total';
        if (totalScore !== null) {
            totalEl.textContent = `Total: ${totalScore}`;
            if (savedGrid.isImported) {
                totalEl.classList.add('imported');
            } else if (isVortex) {
                totalEl.classList.add('vortex');
            }
        } else {
            totalEl.classList.add('incomplete');
            totalEl.textContent = 'Incomplete';
            // Hub indicator appears once the Hub has been edited (has data)
            if (isVortex && savedGrid.vortexGridData !== undefined) {
                const valid = this.persistence.isHubValid(savedGrid.id);
                const hubEl = document.createElement('span');
                hubEl.className = valid ? 'hub-valid' : 'hub-invalid';
                hubEl.textContent = valid ? ' – Hub Valid' : ' – Hub Invalid';
                totalEl.appendChild(hubEl);
            }
        }

        // Per-game scores
        const scoresEl = document.createElement('div');
        scoresEl.className = 'grid-option-scores';
        const clearedInitials: string[] = [];
        for (const gameType of gridGameSet(savedGrid)) {
            const initial = GAME_INITIALS[gameType];
            const best = this.persistence.getBestScore(savedGrid.id, gameType);
            const scoreEl = document.createElement('span');
            scoreEl.className = 'grid-option-score';
            if (best) {
                clearedInitials.push(initial);
                scoreEl.classList.add('solved');
                if (savedGrid.isImported) {
                    scoreEl.classList.add('imported');
                } else if (isVortex) {
                    scoreEl.classList.add('vortex');
                }
                scoreEl.textContent = `${initial}:${best.score.totalScore}`;
            } else {
                scoreEl.textContent = `${initial}:-`;
            }
            scoresEl.appendChild(scoreEl);
        }

        // Compact summary shown instead of the per-game scores on narrow screens,
        // where the 12 score chips squish illegibly: just the cleared initials.
        const clearedEl = document.createElement('div');
        clearedEl.className = 'grid-option-cleared';
        if (savedGrid.isImported) clearedEl.classList.add('imported');
        else if (isVortex) clearedEl.classList.add('vortex');
        clearedEl.textContent = clearedInitials.length
            ? `Cleared: ${clearedInitials.join(' ')}`
            : 'Cleared: —';

        // Challenge label (shown in addition to any vortex/imported label below).
        // Ordered by lifecycle, most-advanced first: a live challenge outranks a
        // scheduled one, which outranks a mere candidate. The lower two are authoring
        // aids (see PersistenceManager.reconcileAuthoringState) and never appear for
        // players; the top one is the only stage the public API can tell us about.
        if (savedGrid.challengeId != null) {
            const labelEl = document.createElement('div');
            labelEl.className = 'grid-option-label challenge-label';
            labelEl.textContent = savedGrid.challengeTitle ? `Challenge · ${savedGrid.challengeTitle}` : 'Challenge';
            info.appendChild(labelEl);
        } else if (savedGrid.scheduledTitle) {
            // Scheduled but not started: absent from /challenges/recent by design, so
            // without this it would read as an ordinary custom grid.
            const labelEl = document.createElement('div');
            labelEl.className = 'grid-option-label scheduled-label';
            const when = savedGrid.scheduledStartsAt ? ` (${savedGrid.scheduledStartsAt.slice(0, 10)})` : '';
            labelEl.textContent = `Scheduled · ${savedGrid.scheduledTitle}${when}`;
            info.appendChild(labelEl);
        } else if (savedGrid.fromBacklog) {
            const labelEl = document.createElement('div');
            labelEl.className = 'grid-option-label backlog-label';
            labelEl.textContent = 'Backlog candidate';
            info.appendChild(labelEl);
        } else if (savedGrid.copyOf) {
            // A native copy of a challenge — otherwise indistinguishable in Custom Grids.
            const labelEl = document.createElement('div');
            labelEl.className = 'grid-option-label copy-label';
            labelEl.textContent = `Copy · ${savedGrid.copyOf}`;
            info.appendChild(labelEl);
        }

        // Label for default, imported, or vortex grids
        if (isDefaultGrid(savedGrid.gridData)) {
            const labelEl = document.createElement('div');
            labelEl.className = 'grid-option-label';
            labelEl.textContent = 'Default grid';
            info.appendChild(labelEl);
        } else if (savedGrid.isImported && isVortex) {
            const labelEl = document.createElement('div');
            labelEl.className = 'grid-option-label imported-label';
            labelEl.textContent = 'Imported · Vortex';
            info.appendChild(labelEl);
        } else if (savedGrid.isImported) {
            const labelEl = document.createElement('div');
            labelEl.className = 'grid-option-label imported-label';
            labelEl.textContent = 'Imported';
            info.appendChild(labelEl);
        } else if (isVortex) {
            const labelEl = document.createElement('div');
            labelEl.className = 'grid-option-label vortex-label';
            labelEl.textContent = 'Vortex';
            info.appendChild(labelEl);
        }

        info.appendChild(totalEl);
        info.appendChild(scoresEl);
        info.appendChild(clearedEl);

        option.appendChild(preview);
        option.appendChild(info);

        // Expand button
        const expandBtn = document.createElement('button');
        expandBtn.className = 'grid-option-expand';
        if (isExpanded) expandBtn.classList.add('expanded');
        expandBtn.textContent = '+';
        expandBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleExpand(savedGrid.id);
        });
        option.appendChild(expandBtn);

        option.addEventListener('click', () => {
            if (this.expandedCardId) {
                this.expandedCardId = null;
                this.deleteConfirmPendingId = null;
                this.render();
                return;
            }
            this.selectGrid(savedGrid.id);
        });

        wrapper.appendChild(option);

        // Action row (when expanded)
        if (isExpanded) {
            wrapper.appendChild(this.renderActionRow(savedGrid, totalScore));
        }

        return wrapper;
    }

    /** A card for the active challenge (clicking adds it to the portfolio + plays it). */
    private renderChallengeCard(challenge: ChallengeRef): HTMLElement {
        const card = document.createElement('div');
        card.className = 'grid-challenge-card';

        const preview = document.createElement('canvas');
        preview.className = 'grid-option-preview';
        preview.width = 80;
        preview.height = 80;
        try { renderGridPreview(preview, challenge.gridData, { isVortex: !!challenge.isVortex }); } catch { /* bad data */ }

        const info = document.createElement('div');
        info.className = 'grid-option-info';
        const title = document.createElement('div');
        title.className = 'grid-option-label challenge-label';
        title.textContent = challenge.title;
        info.appendChild(title);

        card.appendChild(preview);
        card.appendChild(info);
        card.addEventListener('click', () => this.callbacks.onChallengeSelected(challenge));
        return card;
    }

    private renderCreateRow(): HTMLElement {
        const createRow = document.createElement('div');
        createRow.className = 'grid-create-row';
        const createIcon = document.createElement('div');
        createIcon.className = 'grid-create-icon';
        createIcon.textContent = '+';
        const createLabel = document.createElement('div');
        createLabel.className = 'grid-create-label';
        createLabel.textContent = 'Create new custom Grid';
        createRow.appendChild(createIcon);
        createRow.appendChild(createLabel);
        createRow.addEventListener('click', () => this.callbacks.onCreateNew());
        return createRow;
    }

    private renderImportSection(): HTMLElement {
        const importSection = document.createElement('div');
        importSection.className = 'grid-import-section';

        const importIcon = document.createElement('div');
        importIcon.className = 'grid-import-icon';
        importIcon.textContent = '+';

        // Two labels, one shown at a time (CSS picks per view mode): the full name
        // doesn't fit beside the paste field on a phone.
        const importLabel = document.createElement('div');
        importLabel.className = 'grid-import-label';
        const importLabelFull = document.createElement('span');
        importLabelFull.className = 'grid-import-label-full';
        importLabelFull.textContent = 'Load Grid/Solution';
        const importLabelShort = document.createElement('span');
        importLabelShort.className = 'grid-import-label-short';
        importLabelShort.textContent = 'Load…';
        importLabel.append(importLabelFull, importLabelShort);

        const importInput = document.createElement('input');
        importInput.type = 'text';
        importInput.className = 'grid-import-input';
        importInput.placeholder = 'Paste code…';

        const importBtn = document.createElement('button');
        importBtn.className = 'grid-import-btn';
        importBtn.textContent = 'Load';
        importBtn.addEventListener('click', () => {
            this.handleImport(importInput.value);
            importInput.value = '';
        });

        importInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                this.handleImport(importInput.value);
                importInput.value = '';
            }
        });

        importSection.appendChild(importIcon);
        importSection.appendChild(importLabel);
        importSection.appendChild(importInput);
        importSection.appendChild(importBtn);
        return importSection;
    }

    private renderRemoveRow(): HTMLElement {
        const removeRow = document.createElement('div');
        removeRow.className = 'grid-remove-row';
        const removeIcon = document.createElement('div');
        removeIcon.className = 'grid-remove-icon';
        removeIcon.textContent = '×';
        const removeLabel = document.createElement('div');
        removeLabel.className = 'grid-remove-label';
        removeLabel.textContent = 'Remove empty grids';
        removeRow.appendChild(removeIcon);
        removeRow.appendChild(removeLabel);
        removeRow.addEventListener('click', () => {
            const n = this.persistence.removeEmptyGrids();
            this.callbacks.showMessage(
                n ? `Removed ${n} empty grid${n !== 1 ? 's' : ''}` : 'No empty grids to remove'
            );
            if (n) this.render();
        });
        return removeRow;
    }

    private renderActionRow(savedGrid: SavedGrid, totalScore: number | null): HTMLElement {
        const row = document.createElement('div');
        row.className = 'grid-option-actions';

        // Delete (allowed unless this is the last copy of a default grid)
        if (this.persistence.canDeleteGrid(savedGrid.id)) {
            const deleteBtn = document.createElement('button');
            deleteBtn.className = 'action-btn action-delete';
            const isPending = this.deleteConfirmPendingId === savedGrid.id;
            deleteBtn.textContent = isPending ? 'Sure?' : 'Delete';
            if (isPending) deleteBtn.classList.add('confirm');
            deleteBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.handleDelete(savedGrid.id);
            });
            row.appendChild(deleteBtn);
        }

        // Duplicate (any grid). On an imported grid this is also how you get a native
        // copy, so it keeps the old name there; elsewhere it's the set-swap flow.
        const dupBtn = document.createElement('button');
        dupBtn.className = 'action-btn action-duplicate';
        dupBtn.textContent = savedGrid.isImported ? 'Make Native Copy' : 'Duplicate';
        dupBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.callbacks.onDuplicate(savedGrid);
        });
        row.appendChild(dupBtn);

        // Add as Candidate (authoring instance only): promote a grid generated in the
        // app into the backlog, so it can be solved / scheduled like a generated one.
        if (this.callbacks.onAddCandidate && this.persistence.canAddAsCandidate(savedGrid.id)) {
            const addBtn = document.createElement('button');
            addBtn.className = 'action-btn action-add-candidate';
            addBtn.textContent = 'Add as Candidate';
            addBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.callbacks.onAddCandidate!(savedGrid);
            });
            row.appendChild(addBtn);
        }

        // Grid Code (always)
        const gridCodeBtn = document.createElement('button');
        gridCodeBtn.className = 'action-btn action-grid-code';
        gridCodeBtn.textContent = 'Grid Code';
        gridCodeBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.handleCopyGridCode(savedGrid);
        });
        row.appendChild(gridCodeBtn);

        // Challenge Code (always): grid code + this grid's game set, so the recipient
        // plays the same 12 games rather than defaulting to the classic 12.
        const challengeCodeBtn = document.createElement('button');
        challengeCodeBtn.className = 'action-btn action-challenge-code';
        challengeCodeBtn.textContent = 'Challenge Code';
        challengeCodeBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.handleCopyChallengeCode(savedGrid.id);
        });
        row.appendChild(challengeCodeBtn);

        // Solution Code (complete grids only)
        if (totalScore !== null) {
            const solBtn = document.createElement('button');
            solBtn.className = 'action-btn action-solution-code';
            solBtn.textContent = 'Solution Code';
            solBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.handleCopySolutionCode(savedGrid.id);
            });
            row.appendChild(solBtn);
        }

        // Share Win (complete + non-imported)
        if (totalScore !== null && !savedGrid.isImported) {
            const shareBtn = document.createElement('button');
            shareBtn.className = 'action-btn action-share-win';
            shareBtn.textContent = 'Share Win';
            shareBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.handleShareWin(savedGrid, totalScore);
            });
            row.appendChild(shareBtn);
        }

        return row;
    }

    private toggleExpand(gridId: string): void {
        this.deleteConfirmPendingId = null;
        this.expandedCardId = this.expandedCardId === gridId ? null : gridId;
        this.render();
    }

    private handleDelete(gridId: string): void {
        if (this.deleteConfirmPendingId === gridId) {
            // Second click - confirm deletion
            this.deleteConfirmPendingId = null;
            this.expandedCardId = null;
            const success = this.persistence.deleteGrid(gridId);
            if (success) {
                this.render();
            }
        } else {
            // First click - enter confirmation mode
            this.deleteConfirmPendingId = gridId;
            this.render();
        }
    }

    private selectGrid(gridId: string): void {
        if (gridId === this.currentGridId) {
            this.hide();
            this.callbacks.onClose();
            return;
        }

        this.callbacks.onGridSelected(gridId);
        this.hide();
        this.callbacks.onClose();
    }

    private handleCopyGridCode(savedGrid: SavedGrid): void {
        navigator.clipboard.writeText(savedGrid.gridData).then(() => {
            this.callbacks.showMessage('Grid code copied');
        });
    }

    private handleCopyChallengeCode(gridId: string): void {
        const code = this.persistence.exportChallengeCode(gridId);
        if (!code) return;

        navigator.clipboard.writeText(code).then(() => {
            this.callbacks.showMessage('Challenge code copied');
        });
    }

    private handleCopySolutionCode(gridId: string): void {
        const exportString = this.persistence.exportGridWithSolutions(gridId);
        if (!exportString) return;

        navigator.clipboard.writeText(exportString).then(() => {
            this.callbacks.showMessage('Solution code copied');
        });
    }

    private handleShareWin(savedGrid: SavedGrid, totalScore: number): void {
        const text = this.buildShareWinText(savedGrid, totalScore);
        navigator.clipboard.writeText(text).then(() => {
            this.callbacks.showMessage('Share text copied');
        });
    }

    private buildShareWinText(savedGrid: SavedGrid, totalScore: number): string {
        const scores = gridGameSet(savedGrid)
            .map((gameType) => {
                const best = this.persistence.getBestScore(savedGrid.id, gameType);
                return `${GAME_INITIALS[gameType]}:${best?.score.totalScore ?? '-'}`;
            })
            .join(' ');

        return [
            `Grid Games - Score: ${totalScore}`,
            scores,
            savedGrid.gridData,
        ].join('\n');
    }

    private handleImport(exportString: string): void {
        const trimmed = exportString.trim();
        if (!trimmed) return;

        const parsed = this.persistence.parseImportString(trimmed);
        if (!parsed) {
            this.callbacks.showMessage('Invalid grid format');
            return;
        }

        const { gridData, gridConfig, gameStates, vortexHubData, gameSet } = parsed;
        const hasGameStates = Object.keys(gameStates).length > 0;

        if (gridConfig.isVortex && !this.persistence.isVortexUnlocked(gridConfig)) {
            this.callbacks.showMessage('Vortex not unlocked for this grid size');
            return;
        }

        if (hasGameStates) {
            // Full solution import - validate all game states are winning states
            const grid = Grid.deserialize(gridData);
            const validatedStates: Partial<Record<GameType, GameState>> = {};

            // Reconstruct Vortex Hub if present
            let gatePairs: Array<[Position, Position]> | undefined;
            let allGates: Set<string> | undefined;
            if (vortexHubData) {
                const hub = new VortexHub(grid);
                hub.deserialize(vortexHubData);
                const validation = hub.validate();
                if (!validation.isValid) {
                    this.callbacks.showMessage('Invalid Vortex Hub data');
                    return;
                }
                gatePairs = hub.getGatePairs();
                allGates = hub.getGates();
            }

            for (const [initial, serialized] of Object.entries(gameStates)) {
                if (!serialized) continue;

                const gameType = INITIAL_TO_GAME[initial];
                if (!gameType) continue;

                const result = this.validateAndScoreGameState(gameType, grid, serialized, gatePairs, allGates);
                if (!result) {
                    this.callbacks.showMessage(`Invalid ${gameType} solution`);
                    return;
                }

                validatedStates[gameType] = {
                    serialized: serialized,
                    bestSerialized: serialized,
                };
            }

            // All validations passed, add the grid
            const gridId = this.persistence.addImportedGrid(gridData, validatedStates, true, gridConfig, gameSet);
            if (!gridId) {
                this.callbacks.showMessage('Grid limit reached — remove some grids first');
                return;
            }

            // Persist Vortex Hub data on the imported grid
            if (vortexHubData) {
                this.persistence.saveVortexGridData(gridId, vortexHubData);
            }

            this.callbacks.showMessage('Solution imported');
        } else {
            // Grid-only import: hand off to the duplicate modal so the set can be picked
            // before the grid lands. A challenge code arrives with its set pre-chosen.
            if (!this.persistence.canAddGrid()) {
                this.callbacks.showMessage('Grid limit reached — remove some grids first');
                return;
            }
            this.callbacks.onImportGrid(gridData, gridConfig, gameSet);
            return;
        }

        this.render();
    }

    private validateAndScoreGameState(
        gameType: GameType, grid: Grid, serialized: string,
        gatePairs?: Array<[Position, Position]>, allGates?: Set<string>,
    ): { score: ScoreBreakdown } | null {
        const def = GAME_REGISTRY[gameType];
        const game: IGame = new def.Game(grid);

        // Set Vortex gate data before deserializing (gates are validated during deserialization)
        if (gatePairs && allGates && isVortexGame(game)) {
            game.setVortexData(gatePairs, allGates);
        }

        game.deserialize(serialized);
        if (!game.checkWin()) return null;

        const score = game.getScore();
        if (!score) return null;

        return { score };
    }
}
